import React, { useState } from 'react';
import { MapPinIcon, MailIcon, CheckCircleIcon } from 'lucide-react';
import { trackEvent, trackConversion } from '../utils/analytics';
import emailjs from '@emailjs/browser';
export function ContactSection() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    submitted: false
  });
  const MAILJS_SETTINGS = {
    service: 'service_tk09yco',
    template: 'new_template_response'
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      // Send email using EmailJS
      await emailjs.send(MAILJS_SETTINGS.service, MAILJS_SETTINGS.template, {
        from_name: formState.name,
        from_email: formState.email,
        phone: formState.phone,
        message: formState.message
      }, 'zo2Wn_Xk_PcAvBNsX');
      // Track form submission event in Analytics
      trackEvent('contact_form_submission', {
        name: formState.name,
        email: formState.email
      });
      // Track Google Ads conversion (only once, on form submit)
      trackConversion('CONTACT_FORM_SUBMIT', 1.0, 'EUR');
      setFormState(prev => ({
        ...prev,
        submitted: true
      }));
      // Reset form after 30 seconds
      setTimeout(() => {
        setFormState({
          name: '',
          email: '',
          phone: '',
          message: '',
          submitted: false
        });
      }, 30000);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to send message. Please try again or contact us directly at info@nurahealth.eu');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleViewPackages = () => {
    // Track as a regular event, not a conversion
    // This is just navigation after form submission
    trackEvent('packages_viewed_after_contact', {
      source: 'contact_form_success'
    });
  };
  return <section id="contact" className="py-20 bg-gradient-to-b from-white to-slate-50" data-id="element-76">
      <div className="container mx-auto px-4" data-id="element-77">
        <div className="text-center mb-16" data-id="element-78">
          <div className="inline-block bg-blue-50 rounded-full px-4 py-2 mb-4" data-id="element-79">
            <span className="text-blue-700 font-semibold text-sm" data-id="element-80">
              Get in Touch
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-81">
            Start Your <span className="text-blue-600" data-id="element-82">Journey Today</span>
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto text-lg" data-id="element-83">
            We're here to answer your questions and help you start your
            fertility journey. Reach out to schedule a consultation.
          </p>
        </div>
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-84">
          <div className="lg:w-1/2" data-id="element-85">
            <div className="bg-white border border-slate-100 p-8 rounded-3xl shadow-lg h-full" data-id="element-86">
              <h3 className="text-2xl font-bold text-slate-900 mb-6" data-id="element-87">
                Send Us a Message
              </h3>
              {formState.submitted ? <div className="flex flex-col items-center justify-center py-12" data-id="element-88">
                  <div className="bg-green-600 p-4 rounded-full mb-4" data-id="element-89">
                    <CheckCircleIcon className="text-white" size={48} data-id="element-90" />
                  </div>
                  <h4 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-91">
                    Thank You!
                  </h4>
                  <p className="text-slate-600 text-center mb-6" data-id="element-92">
                    We've received your message and will contact you shortly to
                    discuss your fertility journey.
                  </p>
                  <a href="/packages" onClick={handleViewPackages} className="inline-block bg-blue-600 text-white px-8 py-4 rounded-full hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-600/30 font-semibold" data-id="element-93">
                    View Treatment Packages
                  </a>
                </div> : <form onSubmit={handleSubmit} data-id="element-94">
                  {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl" data-id="element-95">
                      <p className="text-red-600 text-sm" data-id="element-96">{error}</p>
                    </div>}
                  <div className="mb-4" data-id="element-97">
                    <label htmlFor="name" className="block text-slate-700 mb-2 font-medium" data-id="element-98">
                      Full Name
                    </label>
                    <input type="text" id="name" name="name" value={formState.name} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" placeholder="John Doe" required disabled={isSubmitting} data-id="element-99" />
                  </div>
                  <div className="mb-4" data-id="element-100">
                    <label htmlFor="email" className="block text-slate-700 mb-2 font-medium" data-id="element-101">
                      Email Address
                    </label>
                    <input type="email" id="email" name="email" value={formState.email} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" placeholder="john@example.com" required disabled={isSubmitting} data-id="element-102" />
                  </div>
                  <div className="mb-4" data-id="element-103">
                    <label htmlFor="phone" className="block text-slate-700 mb-2 font-medium" data-id="element-104">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formState.phone} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" placeholder="(555) 123-4567" disabled={isSubmitting} data-id="element-105" />
                  </div>
                  <div className="mb-6" data-id="element-106">
                    <label htmlFor="message" className="block text-slate-700 mb-2 font-medium" data-id="element-107">
                      Your Message
                    </label>
                    <textarea id="message" name="message" value={formState.message} onChange={handleChange} rows={4} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none" placeholder="How can we help? (optional)" disabled={isSubmitting} data-id="element-108"></textarea>
                  </div>
                  <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-4 rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-blue-600/30 font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-109">
                    {isSubmitting ? 'Sending...' : 'Request Consultation'}
                  </button>
                </form>}
            </div>
          </div>
          <div className="lg:w-1/2 space-y-6" data-id="element-110">
            <div className="bg-white border border-slate-100 p-6 rounded-3xl hover:shadow-lg transition-all group" data-id="element-111">
              <div className="flex items-start" data-id="element-112">
                <div className="bg-blue-600 p-4 rounded-2xl mr-4 group-hover:scale-110 transition-transform" data-id="element-113">
                  <MapPinIcon className="text-white" size={24} data-id="element-114" />
                </div>
                <div data-id="element-115">
                  <h4 className="font-bold text-slate-900 mb-2 text-lg" data-id="element-116">
                    Location
                  </h4>
                  <p className="text-slate-600" data-id="element-117">Prague, Czech Republic</p>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-100 p-6 rounded-3xl hover:shadow-lg transition-all group" data-id="element-118">
              <div className="flex items-start" data-id="element-119">
                <div className="bg-blue-500 p-4 rounded-2xl mr-4 group-hover:scale-110 transition-transform" data-id="element-120">
                  <MailIcon className="text-white" size={24} data-id="element-121" />
                </div>
                <div data-id="element-122">
                  <h4 className="font-bold text-slate-900 mb-2 text-lg" data-id="element-123">
                    Email
                  </h4>
                  <p className="text-slate-600" data-id="element-124">info@nurahealth.eu</p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-100 p-6 rounded-3xl" data-id="element-125">
              <h4 className="font-bold text-slate-900 mb-2 text-lg" data-id="element-126">
                Virtual Consultations
              </h4>
              <p className="text-slate-600 mb-4" data-id="element-127">
                We offer secure video consultations for patients who prefer
                remote appointments or live outside our area.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}